import lazyWithRetry from 'helpers/lazyWithRetry';
import { Navigate } from 'react-router-dom';

const DashboardPage = lazyWithRetry(() => import('../pages/dashboard/DashboardPage'));
const TenantManagementPage = lazyWithRetry(() => import('../pages/tenants/TenantManagementPage'));
const BrandingPage = lazyWithRetry(() => import('../pages/branding/BrandingPage'));
const ServiceFeesPage = lazyWithRetry(() => import('../pages/revenue/ServiceFeesPage'));
const MarkupRulesPage = lazyWithRetry(() => import('../pages/revenue/MarkupRulesPage'));
const DealCodesPage = lazyWithRetry(() => import('../pages/revenue/DealCodesPage'));
const DropnetPage = lazyWithRetry(() => import('../pages/revenue/DropnetPage'));
const DiscountsPage = lazyWithRetry(() => import('../pages/revenue/DiscountsPage'));
const SupplierConfigPage = lazyWithRetry(() => import('../pages/suppliers/SupplierConfigPage'));
const SupplierPartnerMappingPage = lazyWithRetry(() => import('../pages/suppliers/SupplierPartnerMappingPage'));
const AirlinesConfigPage = lazyWithRetry(() => import('../pages/suppliers/AirlinesConfigPage'));
const UsersRolesPage = lazyWithRetry(() => import('../pages/users/UsersRolesPage'));
const FeatureFlagsPage = lazyWithRetry(() => import('../pages/features/FeatureFlagsPage'));
const GstPage = lazyWithRetry(() => import('../pages/gst/GstPage'));
const BookingListPage = lazyWithRetry(() => import('../pages/bookings/BookingListPage'));
const BookingDetailPage = lazyWithRetry(() => import('../pages/bookings/BookingDetailPage'));
const ManualHotelBookingPage = lazyWithRetry(() => import('../pages/bookings/ManualHotelBookingPage'));
const ManualFlightBookingPage = lazyWithRetry(() => import('../pages/bookings/ManualFlightBookingPage'));
const CustomerListPage = lazyWithRetry(() => import('../pages/customers/CustomerListPage'));
const CustomerDetailPage = lazyWithRetry(() => import('../pages/customers/CustomerDetailPage'));
const ServicingPage = lazyWithRetry(() => import('../pages/servicing/ServicingPage'));
const FinancePage = lazyWithRetry(() => import('../pages/finance/FinancePage'));
const CommunicationHubPage = lazyWithRetry(() => import('../pages/communications/CommunicationHubPage'));
const ReportsPage = lazyWithRetry(() => import('../pages/reports/ReportsPage'));
const ProfilePage = lazyWithRetry(() => import('../pages/profile/ProfilePage'));
const NotFoundPage = lazyWithRetry(() => import('../pages/NotFoundPage'));
const TravelPage = lazyWithRetry(() => import('../pages/travel/TravelPage'));

const protectedRoutes = [
  { index: true, element: DashboardPage, title: 'Dashboard' },
  /**
   * Embedded traveller-app surfaces — all three the SAME component reference,
   * with the surface read from the URL inside it.
   *
   * They used to be three arrow wrappers (`() => <TravelPage surface="chat"/>`).
   * AdminApp renders `element` as `<Element />`, so three arrows are three
   * distinct component TYPES at the same position in the tree, and React has no
   * choice but to unmount one and mount the next on every surface switch. That
   * tore down `TravelPage`, `ActingPartnerBar` and `TravelAppFrame` together:
   * the bar re-ran both of its effects against the restored selection and
   * re-fired `listPartnerOptions` + `listPartnerUserOptions`, and the frame was
   * rebuilt from scratch, re-booting the whole traveller app inside it.
   *
   * One shared reference makes all three render `<TravelPage />`, so React
   * reconciles instead — which is exactly what the partner portal has always
   * done (`element: <TravelPage surface="chat" />`, one type, three props).
   *
   * The three routes stay separate on purpose: they carry DIFFERENT permissions
   * (`Travel/Start Chat`, `Travel/Book`, `Travel/Chat History` in
   * navPermissions.js), so collapsing them into one `travel/:surface` route
   * would quietly lose per-surface RBAC and the coverage test that guards it.
   */
  { path: 'travel/chat', element: TravelPage, title: 'Start New Chat' },
  { path: 'travel/shop', element: TravelPage, title: 'Shop Travel' },
  { path: 'travel/history', element: TravelPage, title: 'History' },
  { path: 'tenants', element: TenantManagementPage, title: 'Partners' },
  { path: 'branding', element: BrandingPage, title: 'Branding' },
  { path: 'service-fees', element: ServiceFeesPage, title: 'Service Fees' },
  { path: 'markup-rules', element: MarkupRulesPage, title: 'Markups' },
  { path: 'deal-codes', element: DealCodesPage, title: 'Deal Codes' },
  { path: 'dropnet', element: DropnetPage, title: 'Dropnet' },
  { path: 'discounts', element: DiscountsPage, title: 'Discount' },
  { path: 'gst', element: GstPage, title: 'GST' },
  { path: 'suppliers', element: SupplierConfigPage, title: 'Suppliers' },
  { path: 'supplier-mapping', element: SupplierPartnerMappingPage, title: 'Partner Mapping' },
  { path: 'airlines-config', element: AirlinesConfigPage, title: 'Airlines Config' },
  { path: 'users', element: UsersRolesPage, title: 'Users & Roles' },
  { path: 'feature-flags', element: FeatureFlagsPage, title: 'Features' },
  // Approval Queue moved INSIDE Tenant Management as a tab, not its own page —
  // redirect a stale link/bookmark rather than 404 it.
  { path: 'approvals', element: () => <Navigate to="/admin/tenants" replace />, title: 'Approval Queue' },
  { path: 'bookings', element: BookingListPage, title: 'Bookings' },
  { path: 'bookings/manual-hotel', element: ManualHotelBookingPage, title: 'Manual Hotel Booking' },
  { path: 'bookings/manual-flight', element: ManualFlightBookingPage, title: 'Manual Flight Booking' },
  { path: 'bookings/:bookingId', element: BookingDetailPage, title: 'Booking Detail' },
  { path: 'customers', element: CustomerListPage, title: 'Customers' },
  { path: 'customers/:customerId', element: CustomerDetailPage, title: 'Customer Detail' },
  { path: 'servicing', element: ServicingPage, title: 'Requests' },
  { path: 'finance', element: FinancePage, title: 'Billing' },
  { path: 'communications', element: CommunicationHubPage, title: 'Communications' },
  { path: 'reports', element: ReportsPage, title: 'Reports' },
  { path: 'profile', element: ProfilePage, title: 'Profile' },
  { path: '*', element: NotFoundPage, title: 'Not Found' },
];

export default protectedRoutes;
